import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Accueil', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'Notre Équipe', path: '/equipe' },
    { name: 'Avis Clients', path: '/avis' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => {
    if (path === '/' && location.pathname !== '/') return false;
    return location.pathname.startsWith(path);
  };

  return (
    <nav className={`fixed w-full z-50 transition-all duration-500 ${scrolled || location.pathname !== '/' ? 'bg-white/90 backdrop-blur-lg shadow-sm py-3' : 'bg-transparent py-5'}`}>
      <div className="container mx-auto px-6 flex justify-between items-center">
        <Link to="/" className="text-lg sm:text-2xl font-bold text-teal-800 tracking-tight flex items-center gap-2 sm:gap-4 group">
          <div className="relative flex items-center justify-center">
            <div className="absolute inset-0 bg-teal-100 rounded-full blur-md opacity-0 group-hover:opacity-50 transition-opacity duration-300"></div>
            <img 
              src="https://customer-assets.emergentagent.com/job_irremplacable-revamp/artifacts/k4035lb3_Design%20sans%20titre%20%281%29%20%281%29.png" 
              alt="Logo Irremplaçable" 
              className="h-20 w-auto object-contain relative z-10 transform group-hover:scale-105 transition-transform duration-300"
            />
          </div>
          <span className="inline self-center mt-2 whitespace-nowrap">Irremplaçable</span>
        </Link>

        {/* Desktop Menu */}
        <div className="hidden md:flex items-center space-x-8">
          {navLinks.map((link) => (
            <Link
              key={link.name}
              to={link.path}
              className={`font-medium transition-all duration-300 relative group ${
                isActive(link.path) 
                  ? 'text-teal-600' 
                  : 'text-stone-600 hover:text-teal-600'
              }`}
            >
              {link.name}
              <span className={`absolute -bottom-1 left-0 h-0.5 bg-teal-400 transition-all duration-300 ${isActive(link.path) ? 'w-full' : 'w-0 group-hover:w-full'}`}></span>
            </Link>
          ))}
          <Link
            to="/contact"
            className="bg-teal-600 text-white px-6 py-2.5 rounded-full font-medium hover:bg-teal-700 transition-all shadow-lg shadow-teal-600/20 hover:shadow-teal-600/40 hover:-translate-y-0.5"
          >
            Réserver
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden text-stone-800 p-2 hover:bg-stone-100 rounded-lg transition-colors"
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t border-stone-100 overflow-hidden shadow-xl"
          >
            <div className="flex flex-col p-6 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`text-lg font-medium px-4 py-2 rounded-lg transition-colors ${
                    isActive(link.path) ? 'bg-teal-50 text-teal-600' : 'text-stone-700 hover:bg-stone-50'
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  {link.name}
                </Link>
              ))}
              <Link
                to="/contact"
                className="bg-teal-600 text-white px-5 py-3 rounded-xl font-medium text-center hover:bg-teal-700 transition-colors shadow-md"
                onClick={() => setIsOpen(false)}
              >
                Réserver maintenant
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
